import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { ICredential } from "./getCredentials";
import { toast } from "sonner";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

interface IUpdatePasswordParams {
	credential: ICredential;
	newPassword: string;
}

interface IUpdatePasswordState {
	loading: boolean;
	error: string | null;
}

const initialState: IUpdatePasswordState = {
	loading: false,
	error: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
		"Content-Type": "application/json",
	};
};

const getExternalUrl = () => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Credentials`;
};

export const updatePassword = createAsyncThunk<undefined, IUpdatePasswordParams>(
	"passwordManager/updatePassword",
	async ({ credential, newPassword }, { rejectWithValue, dispatch }) => {
		try {
			const body = [
				{
					companyId: credential.companyId,
					vendorId: credential.vendorId,
					username: credential.username,
					password: newPassword,
					notes: "",
					credentialId: credential.credentialId,
					errorMessage: credential.errorMessage ?? "",
				},
			];

			await withPasswordManagerRefresh(dispatch, () =>
				axios.put(getExternalUrl(), body, {
					headers: getAuthHeaders(),
				}),
			);
			toast.success("Password updated");
			return undefined;
		} catch (error: unknown) {
			const message = error instanceof Error ? error.message : "Failed to update password";
			toast.error(message);
			return rejectWithValue(message);
		}
	},
);

const updatePasswordSlice = createSlice({
	name: "passwordManager/updatePassword",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(updatePassword.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updatePassword.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(updatePassword.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to update password";
			});
	},
});

export const updatePasswordReducer = updatePasswordSlice.reducer;
